/**
 * AdminSchedulePage.jsx
 * -----------------------------------------------------------------------------
 * "Upcoming Schedule" — the admin operational planning board.
 *
 * Two panels, in display order:
 *   B. Upcoming Schedule (admin-selected meetings not yet converted, plus
 *      meetings already converted into a real Showdown) — shown FIRST, so
 *      the admin's planned showdowns are always the first thing visible.
 *   A. Australian Race Meetings — Next 7 Days (imported candidates)
 *      Admin imports/refreshes the 7-day Racing Australia/Harness/Greyhound
 *      meeting list, then ticks meetings to add to the schedule. Every
 *      refresh/import auto-archives past-dated scheduled/showdown_created
 *      rows (see archivePastScheduledMeetings) — never deleted, since a
 *      showdown_created row's convertedSlateId still points at a real
 *      game_slate.
 *
 * Both panels default to hiding past-dated rows (today or later only). A
 * single "Show past meetings" toggle — shown under the Upcoming Schedule
 * title — reveals past rows (dimmed) in both panels for admin lookback.
 *
 * Upcoming Schedule is NOT a Showdown — creating a Showdown from a scheduled
 * meeting hands off to the existing AdminSetupPage flow (prefilled via
 * ?fromMeeting=), which is the only place game_slates docs are written.
 * -----------------------------------------------------------------------------
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeAllUpcomingMeetings,
  importUpcomingMeetings7Days,
  addMeetingToUpcomingSchedule,
  removeMeetingFromUpcomingSchedule,
  markMeetingSkipped,
  archiveScheduledMeeting,
  archivePastScheduledMeetings,
} from '../../services/upcomingMeetingsService';
import { formatMeetingDateShort, suggestedShowdownTitle } from '../../utils/upcomingMeetingStatus';
import './AdminSchedulePage.css';

const IMPORT_IDLE = 'idle';
const IMPORT_LOADING = 'loading';
const IMPORT_SUCCESS = 'success';
const IMPORT_ERROR = 'error';
const IMPORT_TIMEOUT = 'timeout';

const STATE_OPTIONS = ['ALL', 'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

// TB and GRY are always offered (per the Upcoming Schedule filter brief) even
// before any meetings of that code have been imported. Any other code found
// in the imported data (e.g. HAR) is appended gracefully rather than hidden.
const FIXED_CODE_ORDER = ['TB', 'GRY', 'HAR'];
const CODE_LABELS = { TB: 'Thoroughbreds', GRY: 'Greyhounds', HAR: 'Harness' };

const STATUS_BADGE = {
  candidate: { label: 'Not Scheduled', tone: 'muted' },
  scheduled: { label: 'Scheduled', tone: 'scheduled' },
  showdown_created: { label: 'Showdown Created', tone: 'created' },
  skipped: { label: 'Skipped', tone: 'skipped' },
  archived: { label: 'Archived', tone: 'muted' },
};

export default function AdminSchedulePage() {
  const navigate = useNavigate();

  const [meetings, setMeetings] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [importStatus, setImportStatus] = useState(IMPORT_IDLE);
  const [importResult, setImportResult] = useState(null);

  const [codeFilter, setCodeFilter] = useState('ALL');
  const [stateFilter, setStateFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showAlreadyScheduled, setShowAlreadyScheduled] = useState(true);

  // Shared "Show past meetings" toggle — off by default so both panels only
  // show today-or-later rows. Checking it reveals past-dated rows (dimmed)
  // in BOTH the Upcoming Schedule table and the 7-day importer table, rather
  // than needing two separate checkboxes for what is the same concept.
  const [showPastScheduled, setShowPastScheduled] = useState(false);

  const [busyKey, setBusyKey] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);

  // Local (Australian) calendar date as 'YYYY-MM-DD' — matches the
  // meetingDate string convention on every upcoming_meetings doc. Corrects
  // for timezone offset so AEST/AEDT admins don't get "yesterday" from a
  // naive UTC toISOString() call around local midnight (same pattern as
  // AdminSetupPage.jsx's todayISO() / upcomingMeetingsService's
  // todayLocalIso()). Computed once per mount — this is an admin planning
  // board, not a page left open across a midnight rollover.
  const todayIso = useMemo(() => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  }, []);

  const isPastMeeting = useCallback(
    (m) => !!m.meetingDate && m.meetingDate < todayIso,
    [todayIso]
  );

  useEffect(() => {
    const unsub = subscribeAllUpcomingMeetings(
      (rows) => { setMeetings(rows); setLoaded(true); },
      (err) => { console.error('[AdminSchedulePage] subscribe failed:', err); setLoaded(true); }
    );
    return unsub;
  }, []);

  const flash = (msg, ms = 5000) => {
    setActionMsg(msg);
    if (ms) setTimeout(() => setActionMsg(null), ms);
  };

  const handleImport = useCallback(async () => {
    if (importStatus === IMPORT_LOADING) return;
    setImportStatus(IMPORT_LOADING);
    setImportResult(null);
    try {
      const data = await importUpcomingMeetings7Days({ daysAhead: 7, startOffset: 0 });
      setImportResult(data);
      setImportStatus(IMPORT_SUCCESS);

      // Archive (never delete) past-dated scheduled/showdown_created rows
      // after every re-import so the board never accumulates stale past
      // meetings — see archivePastScheduledMeetings for why archiving is
      // used instead of deleting. Never fatal to the import itself — a
      // failure here just means stale rows linger until the next refresh.
      let archivedPast = 0;
      try {
        const archiveResult = await archivePastScheduledMeetings();
        archivedPast = archiveResult.archived;
      } catch (archiveErr) {
        console.error('[AdminSchedulePage] archivePastScheduledMeetings failed:', archiveErr.code, archiveErr.message, archiveErr);
      }

      const archivedNote = data.archived ? `, ${data.archived} stale removed` : '';
      const archivedPastNote = archivedPast > 0 ? `, ${archivedPast} past meeting${archivedPast === 1 ? '' : 's'} archived` : '';
      flash(`✅ Import complete — ${data.imported} new, ${data.refreshed} refreshed${archivedNote}${archivedPastNote} (${data.total} total meetings found).`, 8000);
    } catch (err) {
      console.error('[importUpcomingMeetings7Days] failed:', err);
      const isTimeout = err.code === 'deadline-exceeded' ||
        err.message?.toLowerCase().includes('timeout') ||
        err.message?.toLowerCase().includes('deadline');
      if (isTimeout) {
        setImportStatus(IMPORT_TIMEOUT);
        flash('⏱️ Import is still running on the server — check back in ~1 minute, new meetings will appear automatically.', 10000);
      } else {
        setImportStatus(IMPORT_ERROR);
        flash(`❌ Import failed: ${err.message}`, 10000);
      }
    } finally {
      setTimeout(() => setImportStatus(IMPORT_IDLE), 12000);
    }
  }, [importStatus]);

  async function runAction(meetingKey, action, fn) {
    setBusyKey(`${meetingKey}:${action}`);
    try {
      await fn(meetingKey);
    } catch (err) {
      console.error(`[AdminSchedulePage] ${action} failed:`, err);
      flash(`❌ ${action} failed: ${err.message}`);
    } finally {
      setBusyKey(null);
    }
  }

  function goCreateShowdown(meeting) {
    const params = new URLSearchParams({
      fromMeeting: meeting.meetingKey,
      trackName: meeting.trackName || '',
      meetingDate: meeting.meetingDate || '',
      state: meeting.state || '',
    });
    navigate(`/admin/setup?${params.toString()}`);
  }

  // Full imported (non-archived) meeting set — the denominator for the
  // "Showing X of Y meetings" count, and the base list every filter below
  // narrows down. Archived meetings are always hidden from this board.
  // Past-dated meetings are ALSO hidden by default (today-or-later only),
  // unless "Show past meetings" is ticked — same shared toggle as the
  // Upcoming Schedule panel below.
  const importedRows = useMemo(() => {
    return meetings
      .filter((m) => m.status !== 'archived')
      .filter((m) => showPastScheduled || !isPastMeeting(m));
  }, [meetings, showPastScheduled, isPastMeeting]);

  // How many otherwise-imported meetings are currently hidden purely because
  // they're past-dated — used to give a clearer empty-state message instead
  // of implying nothing has ever been imported.
  const hiddenPastImportedCount = useMemo(() => {
    if (showPastScheduled) return 0;
    return meetings.filter((m) => m.status !== 'archived' && isPastMeeting(m)).length;
  }, [meetings, showPastScheduled, isPastMeeting]);

  // Date/code filter options are derived from what's actually visible
  // (importedRows), so picking a date from the dropdown never silently
  // yields zero rows because that date was hidden by the past-date filter.
  const availableDates = useMemo(() => {
    const dates = new Set(importedRows.map((m) => m.meetingDate).filter(Boolean));
    return Array.from(dates).sort();
  }, [importedRows]);

  const availableCodes = useMemo(() => {
    const codesInData = new Set(importedRows.map((m) => m.raceCode).filter(Boolean));
    const ordered = FIXED_CODE_ORDER.filter((c) => codesInData.has(c) || c === 'TB' || c === 'GRY');
    const extra = Array.from(codesInData).filter((c) => !FIXED_CODE_ORDER.includes(c)).sort();
    return [...ordered, ...extra];
  }, [importedRows]);

  const candidateRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return importedRows
      .filter((m) => (showAlreadyScheduled ? true : m.status === 'candidate'))
      .filter((m) => codeFilter === 'ALL' || m.raceCode === codeFilter)
      .filter((m) => stateFilter === 'ALL' || m.state === stateFilter)
      .filter((m) => !dateFilter || m.meetingDate === dateFilter)
      .filter((m) => !term || (m.trackName || '').toLowerCase().includes(term));
  }, [importedRows, codeFilter, stateFilter, dateFilter, search, showAlreadyScheduled]);

  const hasActiveFilters = codeFilter !== 'ALL' || stateFilter !== 'ALL' || !!dateFilter || !!search.trim();

  function clearFilters() {
    setCodeFilter('ALL');
    setStateFilter('ALL');
    setDateFilter('');
    setSearch('');
  }

  // Includes 'archived' so a scheduled/showdown_created meeting that just
  // got auto-archived by archivePastScheduledMeetings (or manually via the
  // "Archive" button) doesn't disappear from history — ticking "Show past
  // meetings" below still finds it here, satisfying "the admin can always
  // see historical schedule if needed" without ever deleting anything.
  const scheduledPool = useMemo(
    () => meetings.filter((m) => m.status === 'scheduled' || m.status === 'showdown_created' || m.status === 'archived'),
    [meetings]
  );

  const isHiddenByDefault = useCallback(
    (m) => m.status === 'archived' || isPastMeeting(m),
    [isPastMeeting]
  );

  const scheduledRows = useMemo(() => {
    const visible = showPastScheduled ? scheduledPool : scheduledPool.filter((m) => !isHiddenByDefault(m));
    return [...visible].sort((a, b) => (a.meetingDate || '').localeCompare(b.meetingDate || ''));
  }, [scheduledPool, showPastScheduled, isHiddenByDefault]);

  // "Hidden, not missing" distinction for the Upcoming Schedule empty state
  // — an admin seeing "no meetings scheduled" when 12 are just hidden past
  // rows would be misleading.
  const hiddenPastScheduledCount = useMemo(() => {
    if (showPastScheduled) return 0;
    return scheduledPool.filter(isHiddenByDefault).length;
  }, [scheduledPool, showPastScheduled, isHiddenByDefault]);

  const renderBadge = (status) => {
    const cfg = STATUS_BADGE[status] || { label: status, tone: 'muted' };
    return <span className={`asp-badge asp-badge--${cfg.tone}`}>{cfg.label}</span>;
  };

  return (
    <div className="asp">
      <div className="asp__header">
        <div>
          <h1 className="asp__title">📅 Upcoming Schedule</h1>
          <p className="asp__subtitle">
            Import the next 7 days of Australian race meetings, plan which will become Showdowns,
            and convert them when ready. This is an operational planning board — adding a meeting
            here does NOT create a playable Showdown.
          </p>
        </div>
      </div>

      {actionMsg && <div className="asp__toast">{actionMsg}</div>}

      {/* ── PANEL B: Upcoming Schedule (shown first — the admin's operational
           planning board, so today's/this week's showdown plan is always the
           first thing visible, before the raw import list below) ────────── */}
      <section className="asp-panel">
        <div className="asp-panel__head">
          <h2 className="asp-panel__title">Upcoming Schedule</h2>
        </div>

        <label className="asp-toggle">
          <input
            type="checkbox"
            checked={showPastScheduled}
            onChange={(e) => setShowPastScheduled(e.target.checked)}
          />
          Show past meetings
        </label>

        {scheduledRows.length === 0 ? (
          <p className="asp-empty">
            {hiddenPastScheduledCount > 0
              ? `No upcoming meetings scheduled — ${hiddenPastScheduledCount} past meeting${hiddenPastScheduledCount === 1 ? '' : 's'} hidden (tick "Show past meetings" above to view).`
              : 'No meetings scheduled yet. Add meetings from the 7-day list below.'}
          </p>
        ) : (
          <div className="asp-table-scroll">
          <table className="asp-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Track</th>
                <th>State</th>
                <th>Status</th>
                <th>Suggested Title</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scheduledRows.map((m) => (
                <tr key={m.meetingKey} className={isHiddenByDefault(m) ? 'asp-row--past' : undefined}>
                  <td>{formatMeetingDateShort(m.meetingDate)}</td>
                  <td>{m.trackName}</td>
                  <td>{m.state}</td>
                  <td>{renderBadge(m.status)}</td>
                  <td>{suggestedShowdownTitle(m)}</td>
                  <td className="asp-table__actions">
                    {m.status === 'scheduled' && (
                      <>
                        <button className="asp-btn asp-btn--small asp-btn--primary" onClick={() => goCreateShowdown(m)}>
                          Create Showdown
                        </button>
                        <button
                          className="asp-btn asp-btn--small asp-btn--secondary"
                          disabled={busyKey === `${m.meetingKey}:remove`}
                          onClick={() => runAction(m.meetingKey, 'remove', removeMeetingFromUpcomingSchedule)}
                        >
                          Remove
                        </button>
                        <button
                          className="asp-btn asp-btn--small asp-btn--secondary"
                          disabled={busyKey === `${m.meetingKey}:skip`}
                          onClick={() => runAction(m.meetingKey, 'skip', markMeetingSkipped)}
                        >
                          Skip
                        </button>
                        <button
                          className="asp-btn asp-btn--small asp-btn--danger"
                          disabled={busyKey === `${m.meetingKey}:archive`}
                          onClick={() => runAction(m.meetingKey, 'archive', archiveScheduledMeeting)}
                        >
                          Archive
                        </button>
                      </>
                    )}
                    {(m.status === 'showdown_created' || (m.status === 'archived' && m.convertedSlateId)) && (
                      <button
                        className="asp-btn asp-btn--small asp-btn--secondary"
                        onClick={() => navigate(`/admin/setup/${m.convertedSlateId}`)}
                      >
                        View Showdown →
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {/* ── PANEL A: 7-Day Meeting List (importer) ──────────────────────── */}
      <section className="asp-panel">
        <div className="asp-panel__head">
          <h2 className="asp-panel__title">Australian Race Meetings — Next 7 Days</h2>
          <button
            className="asp-btn asp-btn--primary"
            onClick={handleImport}
            disabled={importStatus === IMPORT_LOADING}
          >
            {importStatus === IMPORT_LOADING ? 'Importing… (can take a few minutes)' : '🔄 Refresh / Import 7-Day Meetings'}
          </button>
        </div>

        <div className="asp-filters">
          <label className="asp-filter-field">
            <span className="asp-filter-label">Date</span>
            <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="asp-select">
              <option value="">All dates</option>
              {availableDates.map((d) => (
                <option key={d} value={d}>{formatMeetingDateShort(d)} ({d})</option>
              ))}
            </select>
          </label>

          <label className="asp-filter-field">
            <span className="asp-filter-label">Code</span>
            <select value={codeFilter} onChange={(e) => setCodeFilter(e.target.value)} className="asp-select">
              <option value="ALL">All codes</option>
              {availableCodes.map((c) => (
                <option key={c} value={c}>{CODE_LABELS[c] || c}</option>
              ))}
            </select>
          </label>

          <label className="asp-filter-field">
            <span className="asp-filter-label">State</span>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="asp-select">
              {STATE_OPTIONS.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All states' : s}</option>)}
            </select>
          </label>

          <label className="asp-filter-field">
            <span className="asp-filter-label">Search</span>
            <input
              className="asp-input"
              placeholder="Search track…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <label className="asp-toggle">
            <input
              type="checkbox"
              checked={showAlreadyScheduled}
              onChange={(e) => setShowAlreadyScheduled(e.target.checked)}
            />
            Show already scheduled
          </label>

          {/* Same state as the checkbox under Upcoming Schedule above — one
              toggle, rendered in both panels so it's obvious it also governs
              this table, since past-dated rows here are hidden by default too. */}
          <label className="asp-toggle">
            <input
              type="checkbox"
              checked={showPastScheduled}
              onChange={(e) => setShowPastScheduled(e.target.checked)}
            />
            Show past meetings
          </label>

          {hasActiveFilters && (
            <button type="button" className="asp-clear-filters" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        {loaded && importedRows.length > 0 && (
          <p className="asp-results-count">
            Showing {candidateRows.length} of {importedRows.length} meetings
          </p>
        )}

        {!loaded ? (
          <p className="asp-empty">Loading meetings…</p>
        ) : importedRows.length === 0 ? (
          <p className="asp-empty">
            {hiddenPastImportedCount > 0
              ? `${hiddenPastImportedCount} imported meeting${hiddenPastImportedCount === 1 ? '' : 's'} hidden because ${hiddenPastImportedCount === 1 ? 'its' : 'their'} date has passed — tick "Show past meetings" above to view.`
              : 'No meetings imported yet — click "Refresh / Import 7-Day Meetings" to fetch the latest list.'}
          </p>
        ) : candidateRows.length === 0 ? (
          <div className="asp-empty">
            <p style={{ margin: 0 }}>No meetings match these filters.</p>
            <button type="button" className="asp-clear-filters" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        ) : (
          <div className="asp-table-scroll">
          <table className="asp-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Track</th>
                <th>State</th>
                <th>Code</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {candidateRows.map((m) => (
                <tr key={m.meetingKey} className={isPastMeeting(m) ? 'asp-row--past' : undefined}>
                  <td>{formatMeetingDateShort(m.meetingDate)}</td>
                  <td>{m.trackName}</td>
                  <td>{m.state}</td>
                  <td>{m.raceCode}</td>
                  <td>{renderBadge(m.status)}</td>
                  <td className="asp-table__actions">
                    {(m.status === 'candidate' || m.status === 'skipped') && (
                      <button
                        className="asp-btn asp-btn--small asp-btn--primary"
                        disabled={busyKey === `${m.meetingKey}:add`}
                        onClick={() => runAction(m.meetingKey, 'add', (k) => addMeetingToUpcomingSchedule({ meetingKey: k }))}
                      >
                        + Add to Schedule
                      </button>
                    )}
                    {m.status === 'scheduled' && (
                      <>
                        <button className="asp-btn asp-btn--small asp-btn--primary" onClick={() => goCreateShowdown(m)}>
                          Create Showdown
                        </button>
                        <button
                          className="asp-btn asp-btn--small asp-btn--secondary"
                          disabled={busyKey === `${m.meetingKey}:remove`}
                          onClick={() => runAction(m.meetingKey, 'remove', removeMeetingFromUpcomingSchedule)}
                        >
                          Remove
                        </button>
                      </>
                    )}
                    {m.status === 'showdown_created' && (
                      <button
                        className="asp-btn asp-btn--small asp-btn--secondary"
                        onClick={() => navigate(`/admin/setup/${m.convertedSlateId}`)}
                      >
                        View Showdown →
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </div>
  );
}
